'use client';

import { Divider } from '@heroui/react';
import { useAction } from '@reatom/npm-react';
import { manrope } from '@trwl/app/fonts';
import { DELIVERY_TEXTS } from '@trwl/constants';
import { StoreProduct } from '@trwl/entities/product/@x';
import { addToCartAction, removeFromCartAction } from '@trwl/store';
import { formatNumber } from '@trwl/util';
import { preprocessImage } from '@trwl/util/preprocessImage';
import cn from 'classnames';
import Image from 'next/image';
import Link from 'next/link';
import { useCallback, useMemo } from 'react';

import { Counter } from './components/Counter';

const SCALE = 3;

const CartBlock = ({
  cart,
  title,
  width = 120,
  withCounter = false,
}: {
  cart: StoreProduct[];
  title: string;
  width?: number;
  withCounter?: boolean;
}) => {
  const addToCart = useAction(addToCartAction);
  const removeFromCart = useAction(removeFromCartAction);

  if (!cart) return null;
  return (
    <div className={'w-full flex flex-col gap-[7px]'}>
      <div
        className={cn(
          'text-[16px] font-normal text-[#282828]',
          manrope.className,
        )}
      >
        {title}
      </div>
      <Divider />
      <div className={'flex flex-col w-full gap-5'}>
        {cart.map((product) => (
          <div
            key={'cart-row-' + product.id}
            className="w-full flex flex-row justify-between items-center gap-2 md:gap-6"
          >
            <div className={'min-w-[120px] w-[120px]'}>
              <Link href={product.url || '#'}>
                <Image
                  className={'w-full'}
                  src={preprocessImage(product.image, 300, 480)}
                  alt={product.name}
                  width={width * SCALE}
                  height={(width / 120) * 75 * SCALE}
                />
              </Link>
            </div>

            <div
              className={
                'flex flex-col md:flex-row md:items-start w-full gap-[10px]'
              }
            >
              <div className={'flex flex-col flex-grow w-full gap-[10px]'}>
                <Link href={product.url || '#'}>
                  <div
                    className={
                      'text-[14px] md:text-[16px] text-[#282828] font-medium leading-[20px]'
                    }
                  >
                    {product.name}
                  </div>
                </Link>
                <div
                  className={cn(
                    manrope.className,
                    'text-[#A0A0A0] text-[12px] md:text-[13px] font-medium  text-ellipsis line-clamp-1',
                  )}
                >
                  {(product.properties || [])
                    .map((property) =>
                      [property.key, property.value].join(': '),
                    )
                    .join(', ')}
                </div>
              </div>
              <div
                className={cn(
                  'flex flex-row gap-5 items-center',
                  withCounter
                    ? 'justify-between md:justify-end'
                    : 'justify-end',
                )}
              >
                {withCounter && (
                  <div>
                    <Counter
                      count={product.count}
                      onDecrement={() => removeFromCart(product)}
                      onIncrement={() => addToCart(product)}
                    />
                  </div>
                )}
                <div
                  className={
                    'text-[#10181C] font-medium text-[16px] text-end text-nowrap min-w-[80px]'
                  }
                >
                  {formatNumber(product.price)} ₽
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const Cart = ({
  cart,
  className,
  withCounter,
  width,
  days,
}: {
  cart: StoreProduct[];
  withCounter?: boolean;
  className?: string;
  width?: number;
  days?: number;
}) => {
  const { withAirplane, withoutAirplane } = useMemo(() => {
    const withAirplane = new Array<StoreProduct>();
    const withoutAirplane = new Array<StoreProduct>();
    cart.forEach((product) => {
      const arr =
        product.deliveryType === 'AIRPLANE' ? withAirplane : withoutAirplane;
      arr.push(product);
    });
    return { withAirplane, withoutAirplane };
  }, [cart]);

  const getMaxDeliveryDay = useCallback((products: StoreProduct[]) => {
    return Math.max(...products.map((t) => t.maxDelivery ?? 0));
  }, []);

  return (
    <div
      className={cn('flex flex-col gap-[60px] max-w-[750px] w-full', className)}
    >
      {withAirplane.length > 0 && (
        <CartBlock
          width={width}
          cart={withAirplane}
          withCounter={withCounter}
          title={`Экспресс-доставка ${DELIVERY_TEXTS.AIRPLANE(days === undefined ? getMaxDeliveryDay(withAirplane) : days)}.`}
        />
      )}
      {withoutAirplane.length > 0 && (
        <CartBlock
          width={width}
          cart={withoutAirplane}
          withCounter={withCounter}
          title={`Обычная доставка ${DELIVERY_TEXTS.DEFAULT(days === undefined ? getMaxDeliveryDay(withoutAirplane) : days)}`}
        />
      )}
    </div>
  );
};
